import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import { FC, useEffect, useRef, useState } from 'react';
import {
  EmailThreadModalWrapper,
  MessageDotsWrapper,
} from 'pages/accountDrawer/emailView/styles';
import { EmailMessage } from 'crono-fe-common/types/emailMessage';
import { Prospect } from 'crono-fe-common/types/prospect';
import useGetThread from 'hooks/services/email/useGetThread';
import useGetLogEmail from 'hooks/services/event/useGetLogEmail';
import { Avatar, CircularProgress, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useAuth } from 'context/auth';
import { User } from 'crono-fe-common/types/user';
import { splitMessageContent, cleanPixel } from 'utils/businessLogic';
import { ReactComponent as ErrorEmailThread } from 'assets/images/Error_email_thread.svg';
import { getImageFromUrl } from 'crono-fe-common/utils';
import { colors } from 'crono-fe-common/theme';
import IconBottomArrow from 'crono-fe-common/icons/Icon-Bottom-Arrow';
import { useNavigate } from 'react-router-dom';
import PATH from 'crono-fe-common/constants/path';
import useRedirectToInbox from 'context/customLocation/redirectToInbox';
import { useEmailThread } from './context';
import { EmailThreadContent } from './types';
import IconExpandHorizontal from 'crono-fe-common/icons/Icon-Expand-Horizontal';
import { CronoErrorCode } from 'crono-fe-common/types/enums/cronoErrorCode';
import { Button, CloseMdIcon, IconButton } from '@crono-one/design-system';

interface EmailThreadModalProps extends EmailThreadContent {
  threadId: string | null;
  close: () => void;
}

// Email content resolution order:
// 1. Fetch full thread via API (useGetThread) — requires threadId + owned mailbox
// 2. Fall back to activity.emailLog or logEmail prop (single message with optional reply)
// 3. Fall back to activity.subject + activity.description (no sender/receiver shown)
const EmailThreadModal: FC<EmailThreadModalProps> = ({
  account,
  prospect,
  openReply,
  owned,
  threadId,
  activity,
  logEmail: _logEmail,
  close,
  onRedirectToSettings,
}) => {
  const logEmailToUse = activity?.emailLog ?? _logEmail;
  const { user } = useAuth();

  const [firstMessagesToRender, setFirstMessagesToRender] = useState<
    EmailMessage[] | null
  >(null);

  const [collapsedMessagesToRender, setCollapsedMessagesToRender] = useState<
    EmailMessage[] | null
  >(null);

  const [lastMessageToRender, setLastMessageToRender] =
    useState<EmailMessage | null>(null);

  const [showCollapsedEmails, setShowCollapsedEmails] =
    useState<boolean>(false);

  const [countHovered, setCountHovered] = useState<boolean>(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<string[]>([]);

  const emailRef = useRef<HTMLDivElement>(null);
  const ccRef = useRef<HTMLDivElement>(null);

  const [isEllipsed, setIsEllipsed] = useState(false);
  const [isCcEllipsed, setIsCcEllipsed] = useState(false);

  useEffect(() => {
    const checkEllipsis = () => {
      if (emailRef.current) {
        setIsEllipsed(
          emailRef.current.scrollWidth > emailRef.current.clientWidth,
        );
      }
      if (ccRef.current) {
        setIsCcEllipsed(ccRef.current.scrollWidth > ccRef.current.clientWidth);
      }
    };

    const resizeObserver = new ResizeObserver(checkEllipsis);

    if (emailRef.current) {
      resizeObserver.observe(emailRef.current);
    }

    if (ccRef.current) {
      resizeObserver.observe(ccRef.current);
    }

    checkEllipsis();

    return () => {
      resizeObserver.disconnect();
    };
  }, [emailRef, ccRef, firstMessagesToRender]);

  const {
    data: threadMessages,
    isInitialLoading: isInitialLoadingThreadMessages,
    isFetching: isFetchingThreadMessages,
    isError: isErrorGetThread,
    error: errorGetThread,
  } = useGetThread(threadId ?? '', !!threadId && owned);

  // Strategy details sends email logs without the body; fetch it only when the thread won't supply it.
  const isFallbackNeeded =
    !isFetchingThreadMessages && !threadMessages?.data?.data?.length;
  const replyEmailLog = logEmailToUse?.replyEmail;
  const { data: fetchedEmail, isInitialLoading: isLoadingEmailBody } =
    useGetLogEmail(
      logEmailToUse?.id,
      isFallbackNeeded && !logEmailToUse?.contentHtml,
    );
  const { data: fetchedReplyEmail, isInitialLoading: isLoadingReplyBody } =
    useGetLogEmail(
      replyEmailLog?.id,
      isFallbackNeeded && !!replyEmailLog && !replyEmailLog.contentHtml,
    );
  const isLoadingLogBodies = isLoadingEmailBody || isLoadingReplyBody;
  const emailBodyHtml =
    fetchedEmail?.data?.data?.contentHtml || logEmailToUse?.contentHtml;
  const replyBodyHtml =
    fetchedReplyEmail?.data?.data?.contentHtml || replyEmailLog?.contentHtml;

  useEffect(() => {
    let messages: EmailMessage[] | undefined = threadMessages?.data?.data;
    //Let's use the emailLog from the task if there is no messages from the thread
    if (!messages || messages.length === 0) {
      if (isLoadingLogBodies) {
        setFirstMessagesToRender(null);
        setCollapsedMessagesToRender(null);
        setLastMessageToRender(null);
        return;
      }
      if (logEmailToUse || activity) {
        const logMessages: EmailMessage[] = [
          {
            id: `${activity?.id ?? logEmailToUse?.id}`,
            cc: logEmailToUse?.cc ?? null,
            content: emailBodyHtml || activity?.description || '',
            receiver: logEmailToUse?.to ?? '',
            sender: logEmailToUse?.from ?? '',
            senderName: null,
            sentTime: logEmailToUse?.eventDatetime ?? new Date(),
            sentFromUser: true,
            subject: logEmailToUse?.subject ?? activity?.subject ?? '',
            threadId: logEmailToUse?.threadId ?? '',
            to: [logEmailToUse?.to ?? ''],
          },
        ];

        const replyEmail = logEmailToUse?.replyEmail;
        if (replyEmail) {
          logMessages.push({
            id: `reply-${replyEmail.id}`,
            cc: replyEmail.cc ?? null,
            content: replyBodyHtml ?? '',
            receiver: replyEmail.to ?? '',
            sender: replyEmail.from ?? '',
            senderName: null,
            sentTime: replyEmail.eventDatetime ?? new Date(),
            sentFromUser: false,
            subject: replyEmail.subject ?? '',
            threadId: replyEmail.threadId ?? '',
            to: [replyEmail.to ?? ''],
          });
        }

        messages = logMessages;
      }
    }
    if (messages?.length) {
      if (messages.length > 5) {
        setFirstMessagesToRender(messages.slice(0, 4));
        setCollapsedMessagesToRender(messages.slice(4, messages.length - 1));
        setLastMessageToRender(messages[messages.length - 1]);
      } else {
        setFirstMessagesToRender(messages.slice(0, messages.length - 1));
        setCollapsedMessagesToRender(null);
        setLastMessageToRender(messages[messages.length - 1]);
      }

      setSelectedMessageIds([messages[messages.length - 1].id]);
    }
  }, [
    threadMessages,
    isErrorGetThread,
    logEmailToUse,
    activity,
    isLoadingLogBodies,
    emailBodyHtml,
    replyBodyHtml,
  ]);

  //ids of the messages that user want to see the replies
  const [showRepliesIds, setShowRepliesIds] = useState<string[]>([]);

  const { setEmailThreadContent } = useEmailThread();

  const redirectToInbox = useRedirectToInbox({
    onRedirect: () => {
      setEmailThreadContent(null);
      close();
    },
  });

  const renderMessage = (
    message: EmailMessage,
    user: User,
    prospect: Prospect,
    noBorder?: boolean,
  ) => {
    const clearMessage: EmailMessage = cleanPixel(message);
    const { mainMessage, replies } = splitMessageContent(clearMessage.content);
    const hasEmailParticipants =
      !!clearMessage.sender || clearMessage.to.some((t) => !!t);

    const handleSelectMessage = (message: EmailMessage) => {
      if (selectedMessageIds.includes(message.id)) {
        setSelectedMessageIds(
          selectedMessageIds.filter((id) => id !== message.id),
        );
        //also removed from replies, if the user open again a message, will have the replies closed by default
        setShowRepliesIds(showRepliesIds.filter((id) => id !== message.id));
      } else {
        setSelectedMessageIds([...selectedMessageIds, message.id]);
      }
    };

    const toggleShowReplies = () => {
      if (showRepliesIds.includes(message.id)) {
        setShowRepliesIds(showRepliesIds.filter((id) => id !== message.id));
        return;
      }

      setShowRepliesIds([...showRepliesIds, message.id]);
    };

    const emailsTooltipContent = () => {
      const toList = clearMessage.to;
      const ccList = clearMessage.cc || [];

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            padding: '2px 4px',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '4px',
            }}
          >
            <Typography
              fontSize={12}
              lineHeight={'16px'}
              fontWeight={500}
              color={colors.grey2}
            >
              From:
            </Typography>

            <Typography fontSize={12} lineHeight={'16px'} fontWeight={500}>
              {clearMessage.sender}
            </Typography>
          </div>

          <div
            style={{
              display: 'flex',
              gap: '4px',
            }}
          >
            <Typography
              fontSize={12}
              lineHeight={'16px'}
              fontWeight={500}
              color={colors.grey2}
            >
              To:
            </Typography>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {toList.map((email, index) => (
                <Typography
                  fontSize={12}
                  lineHeight={'16px'}
                  fontWeight={500}
                  key={index}
                >
                  {email}
                </Typography>
              ))}
            </div>
          </div>

          {!!ccList.length && (
            <div
              style={{
                display: 'flex',
                gap: '4px',
              }}
            >
              <Typography
                fontSize={12}
                lineHeight={'16px'}
                fontWeight={500}
                color={colors.grey2}
              >
                Cc:
              </Typography>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {ccList.map((email, index) => (
                  <Typography
                    fontSize={12}
                    lineHeight={'16px'}
                    fontWeight={500}
                    key={index}
                  >
                    {email}
                  </Typography>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    };
    const isSentFromUser = clearMessage.sentFromUser;
    const senderWholeNameString = `${clearMessage.senderName ? `${clearMessage.senderName} · ` : ''}${clearMessage.sender}`;
    return (
      <FlexDiv
        key={clearMessage.id}
        className="message-container"
        justifyContent="center"
        direction="column"
        height={'unset'}
        style={{
          marginBottom: '16px',
          paddingBottom: !noBorder ? '16px' : 0,
          borderBottom: !noBorder ? `1px solid ${colors.grey444}` : 'none',
        }}
      >
        <FlexDiv
          className={'message-header'}
          justifyContent="space-between"
          onClick={() => handleSelectMessage(clearMessage)}
        >
          <div className="message-header__left">
            <Avatar
              key={prospect.objectId}
              src={
                (isSentFromUser
                  ? user.company?.website &&
                    getImageFromUrl(user.company.website, null)
                  : account?.website &&
                    getImageFromUrl(account.website, null)) ?? undefined
              }
              className="message-avatar"
            >
              {clearMessage.senderName ?? clearMessage.sender}
            </Avatar>

            <div
              className="message-sender"
              style={{
                flex: 1,
                overflow: 'hidden',
              }}
            >
              {hasEmailParticipants && (
                <>
                  <Tooltip
                    enterDelay={400}
                    enterNextDelay={400}
                    title={senderWholeNameString}
                    arrow
                  >
                    <Typography fontSize={14} fontWeight={500} noWrap>
                      {clearMessage.senderName ?? clearMessage.sender}
                      {/*If we have the senderName we show also the sender mail */}
                      {!!clearMessage.senderName && (
                        <span
                          style={{
                            color: colors.grey11,
                            fontSize: 12,
                            fontWeight: 400,
                          }}
                        >
                          {' '}
                          · {clearMessage.sender}
                        </span>
                      )}
                    </Typography>
                  </Tooltip>

                  <div
                    style={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Typography
                      ref={emailRef}
                      fontSize={12}
                      fontWeight={400}
                      color={colors.grey11}
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'inline-block',
                        width: '80%',
                      }}
                      noWrap
                    >
                      to: {clearMessage.to.join(', ')}
                    </Typography>

                    {isEllipsed && (
                      <Tooltip
                        enterDelay={300}
                        enterNextDelay={300}
                        title={emailsTooltipContent()}
                        arrow
                        PopperProps={{
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -10],
                              },
                            },
                          ],
                        }}
                      >
                        <div
                          style={{
                            height: '24px',
                            width: '24px',
                            cursor: 'pointer',
                          }}
                        >
                          <IconBottomArrow />
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  {clearMessage.cc && clearMessage.cc.length > 0 && (
                    <div
                      style={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <Typography
                        ref={ccRef}
                        fontSize={12}
                        fontWeight={400}
                        color={colors.grey11}
                        sx={{
                          width: '80%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-block',
                        }}
                      >
                        cc: {clearMessage.cc.join(', ')}
                      </Typography>

                      {isCcEllipsed && (
                        <Tooltip
                          enterDelay={300}
                          enterNextDelay={300}
                          title={emailsTooltipContent()}
                          arrow
                          PopperProps={{
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, -10],
                                },
                              },
                            ],
                          }}
                        >
                          <div
                            style={{
                              height: '24px',
                              width: '24px',
                              cursor: 'pointer',
                            }}
                          >
                            <IconBottomArrow />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div style={{ width: '180px', flexShrink: 0 }}>
            <Typography
              color={colors.grey11}
              fontWeight={500}
              fontSize={12}
              textAlign={'right'}
            >
              {format(new Date(clearMessage.sentTime), 'MMMM d, yyyy h:mm a')}
            </Typography>
          </div>
        </FlexDiv>

        {selectedMessageIds.includes(clearMessage.id) && (
          <>
            <FlexDiv
              className="message-body"
              justifyContent="space-between"
              direction="column"
            >
              {/* selectedMessage is without pixel, while message not */}
              <div
                dangerouslySetInnerHTML={{
                  __html: mainMessage,
                }}
                className="w-full [&_img]:max-w-full [&_img]:h-auto text-gray-900 text-sm"
              />

              {replies && (
                <MessageDotsWrapper onClick={() => toggleShowReplies()}>
                  <div className="message-dot" />
                  <div className="message-dot" />
                  <div className="message-dot" />
                </MessageDotsWrapper>
              )}
            </FlexDiv>

            {showRepliesIds.includes(message.id) && (
              <FlexDiv
                className="message-body"
                justifyContent="space-between"
                direction="column"
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: replies,
                  }}
                  className="w-full [&_img]:max-w-full [&_img]:h-auto text-neutral-800 text-sm"
                />
              </FlexDiv>
            )}

            {owned &&
              (openReply ? (
                <FlexDiv
                  className="reply-container"
                  direction="row"
                  justifyContent="start"
                >
                  <Button
                    tone="primary"
                    size="sm"
                    className="min-w-30"
                    onClick={() =>
                      openReply({
                        threadId,
                        senderEmail: clearMessage.sender,
                      })
                    }
                  >
                    Reply
                  </Button>

                  {clearMessage.cc &&
                    clearMessage.cc.length > 0 &&
                    clearMessage.cc.some((val: string) => val.length > 0) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-w-30"
                        onClick={() =>
                          openReply({
                            threadId,
                            toAll: true,
                            senderEmail: clearMessage.sender,
                          })
                        }
                      >
                        Reply all
                      </Button>
                    )}
                </FlexDiv>
              ) : (
                <FlexDiv
                  className="reply-container"
                  direction="row"
                  justifyContent="start"
                >
                  <Button
                    size="sm"
                    className="min-w-30"
                    onClick={() => {
                      redirectToInbox(prospect.objectId);
                    }}
                  >
                    Open inbox
                  </Button>
                </FlexDiv>
              ))}
          </>
        )}
      </FlexDiv>
    );
  };
  const navigate = useNavigate();

  const handleGoToSettings = () => {
    navigate(PATH.SETTINGS.INTEGRATIONS.MAILBOX);
    close();
    if (onRedirectToSettings) {
      onRedirectToSettings();
    }
  };

  return (
    <EmailThreadModalWrapper
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <IconButton
          label="Close email drawer"
          icon={CloseMdIcon}
          shape="rounded"
          variant="light"
          tone="lightGray"
          size="lg"
          onClick={close}
        />

        <p className="text-2xl font-bold">
          {threadMessages?.data?.data.at(0)?.subject ??
            logEmailToUse?.subject ??
            activity?.subject ??
            'No subject'}
        </p>
      </div>
      <div
        className="modal-body"
        style={{
          height: '100%',
        }}
      >
        {(isInitialLoadingThreadMessages || isLoadingLogBodies) && (
          <FlexDiv justifyContent="center" style={{ marginTop: '50px' }}>
            <CircularProgress />
          </FlexDiv>
        )}

        {isErrorGetThread &&
          !isInitialLoadingThreadMessages &&
          !isLoadingLogBodies &&
          !lastMessageToRender && (
            <div className="justify-center mt-12">
              <div className="error-mail-container">
                <ErrorEmailThread />
                {errorGetThread.response?.data?.errors?.at(0)?.code ===
                  CronoErrorCode.MailBoxDisconnected &&
                activity?.emailLog?.from ? (
                  <>
                    <p className="text-center font-semibold max-w-md">
                      The {activity.emailLog.from} is disconnected. Please
                      connect it and try again
                    </p>
                    <Button size="lg" onClick={handleGoToSettings}>
                      Connect
                    </Button>
                  </>
                ) : (
                  <Typography
                    fontSize={'16px'}
                    fontWeight={600}
                    lineHeight={'24px'}
                  >
                    Sorry, this thread could not be retrieved
                  </Typography>
                )}
              </div>
            </div>
          )}

        {prospect && user && (
          <>
            {firstMessagesToRender &&
              firstMessagesToRender.length > 0 &&
              firstMessagesToRender.map((message: EmailMessage) =>
                renderMessage(message, user, prospect),
              )}

            {!!collapsedMessagesToRender?.length && (
              <>
                {!showCollapsedEmails ? (
                  <div className="collapsed-container">
                    <div
                      className="collapsed-count"
                      onClick={() => setShowCollapsedEmails(true)}
                      onMouseEnter={() => setCountHovered(true)}
                      onMouseLeave={() => setCountHovered(false)}
                    >
                      {!countHovered ? (
                        collapsedMessagesToRender.length
                      ) : (
                        <IconExpandHorizontal />
                      )}
                    </div>
                    <div className="collapsed-inner" />
                  </div>
                ) : (
                  collapsedMessagesToRender.map((message: EmailMessage) =>
                    renderMessage(message, user, prospect, false),
                  )
                )}
              </>
            )}

            {lastMessageToRender &&
              renderMessage(lastMessageToRender, user, prospect, true)}
          </>
        )}
      </div>
    </EmailThreadModalWrapper>
  );
};

export default EmailThreadModal;
